import Assignment from './Assignment';
import Document from '@/models/read/Document';
import Service from '~/models/read/Service';
import moment from 'moment/moment';

class ProofreadingOrder {
    constructor({
        id,
        attributes: {
            status,
            student,
            deadline,
            dateCreated,
            services,
            assignments,
            term,
            document,
            selectedWords: words,
            introduction,
            editingStyle,
            extra,
            pages,
            hasFootnotes,
            proofreadFootnotes,
            fieldOfStudy,
            levelOfEducation,
            documentType,
            additionalFiles,
            shortTermDeadline,
            sampleEditStudentFeedback,
            sampleEditStudentFeedbackDeadline,
            linkedOrder,
            isDeletionAllowed,
        },
        links,
    }) {
        this._id = id;
        this._status = status;
        this._grammarCheckStatus = 'not_started';
        this._student = student;
        this._hasFootnotes = hasFootnotes;
        this._totalPrice = null;
        this._deadline = new Date(deadline);
        this._paid = new Date(dateCreated);
        this._term = term;
        this._shortTermDeadline = shortTermDeadline;
        document.selectedPages = pages;
        this._document = new Document(document);
        this._words = words;
        this._introduction = introduction;
        this._editingStyle = editingStyle;
        this._extraComments = extra;
        this._services = services ? services.map(s => new Service(s)) : null;
        this._assignments = assignments.map(assignment => new Assignment(assignment, this)).sort((a, b) => (a.startDate > b.startDate) ? 1 : -1);
        this._invoices = [];
        this._proofreadFootnotes = proofreadFootnotes;
        this._fieldOfStudy = fieldOfStudy;
        this._levelOfEducation = levelOfEducation;
        this._documentType = documentType;
        this._editingStyle = editingStyle;
        this._additionalFiles = additionalFiles || [];
        this._sampleEditStudentFeedback = sampleEditStudentFeedback || null;
        this._sampleEditStudentFeedbackDeadline = sampleEditStudentFeedbackDeadline || null;
        this._links = links || {};
        this._linkedOrder = linkedOrder || null;
        this._isDeletionAllowed = isDeletionAllowed || false;
    }

    get id() {
        return this._id;
    }

    get isDeletionAllowed() {
        return this._isDeletionAllowed;
    }

    get deadline() {
        return this._deadline;
    }

    get paid() {
        return this._paid;
    }

    get placedAt() {
        return this._paid;
    }

    get term() {
        return this._term;
    }

    get shortTermDeadline() {
        return this._shortTermDeadline / 60 || null;
    }

    get status() {
        return this._status;
    }

    set grammarCheckStatus(status) {
        this._grammarCheckStatus = status;
    }

    get grammarCheckStatus() {
        return this._grammarCheckStatus;
    }

    get student() {
        return this._student;
    }

    get totalPrice() {
        return this._totalPrice;
    }

    get services() {
        return this._services;
    }

    // for a bundle assignment do not list P&E "child services"
    get groupedServices() {
        if (this.hasBundledAssignment) {
            let services = [];
            this._services.forEach(s => {
                if (s.type === 'structure_check' || s.type === 'clarity_check') {
                    return;
                }
                services.push(s);
            });

            return services;
        }
        return this._services;
    }

    get document() {
        return this._document;
    }

    get words() {
        return this._words;
    }

    get timeToGoThroughTheDocument() {
        return 1 + Math.ceil(this._words / 5000);
    }

    get introduction() {
        return this._introduction;
    }

    get editingStyle() {
        return this._editingStyle;
    }

    get extraComments() {
        return this._extraComments;
    }

    get assignments() {
        return this._assignments;
    }

    get lastSampleEdit() {
        return this._assignments.find(a => a.nextAssignments.find(na => na.isEditingService || na.isBundle));
    }

    get hasSampleEditInProgress() {
        return this.lastSampleEdit && this.lastSampleEdit.isInProgress;
    }

    get hasSampleEditAssignmentReadyForReview() {
        return this.lastSampleEdit && this.lastSampleEdit.isDelivered &&
          this.lastSampleEdit.nextAssignments.find(na => (na.isEditingService || na.isBundle) && na.isInProgress);
    }

    get hasFootnotes() {
        return this._hasFootnotes;
    }

    get proofreadFootnotes() {
        return this._proofreadFootnotes;
    }

    get fieldOfStudy() {
        return this._fieldOfStudy;
    }

    get levelOfEducation() {
        return this._levelOfEducation;
    }

    get documentType() {
        return this._documentType;
    }

    get hasLayoutCheck() {
        return this._services.findIndex(service => service.type === 'layout_check') !== -1;
    }

    get hasReferenceCheck() {
        return this._services.findIndex(service => service.type === 'reference_check') !== -1;
    }

    get hasExpertReview() {
        return this._services.findIndex(service => service.type === 'expert_review') !== -1;
    }

    get hasSampleEdit() {
        return this._assignments.findIndex(assignment => assignment.isSampleEdit) !== -1;
    }

    get hasDeliveredSampleEdit() {
        return this.lastSampleEdit && this.lastSampleEdit.isDelivered;
    }

    get deliveredAssignments() {
        return this._assignments.filter(assignment => assignment.isDelivered);
    }

    get assignmentsWithFeedback() {
        return this._assignments.filter(assignment => assignment.hasFeedback && (assignment.isCompleted || assignment.isDelivered));
    }

    get canGiveThankYouAssignments() {
        if (this.firstReviewScore) {
            return [];
        }
        return this._assignments.filter(assignment => assignment.canGiveThankYou);
    }

    get hasDeliveredAssignment() {
        return this.deliveredAssignments.length !== 0;
    }

    addInvoices(invoices) {
        this._invoices = invoices;
    }

    get additionalPaymentInvoices() {
        return this._invoices.filter(i => i.isOpen() && i.total.isPositive());
    }

    get refundInvoices() {
        return this._invoices.filter(i => i.isOpen() && i.total.isNegative());
    }

    get hasMultipleEditingServiceAssignments() {
        return this._assignments.filter(a => a.isEditingService).length > 1;
    }

    get allAssignmentsAreDelivered() {
        return !this._assignments.find(a => !a.hasNextAssignment && !a.isDelivered && !a.isSampleEdit);
    }

    get firstReviewScore() {
        let assignment = this.assignments.find(assignment => assignment.reviewScore > 0);

        if (!assignment) {
            return null;
        }

        return assignment.reviewScore;
    }

    get details() {
        let groups = {
            proofreading: ['proofreading_and_editing', 'structure_check', 'clarity_check', 'gender_editing'],
            layout: ['layout_check'],
            reference: ['reference_check'],
            essay: ['essay_editing_and_coaching'],
            expertReview: ['expert_review'],
        };

        let details = {
            proofreading: [],
            layout: [],
            reference: [],
            essay: [],
            expertReview: [],
        };

        let service;

        Object.keys(groups).forEach((group) => {
            groups[group].forEach((serviceType) => {
                service = this._services.find(service => service.type === serviceType);
                if (!service || !service.details || !service.details.questions) {
                    return;
                }
                Object.values(service.details.questions).forEach((rawQuestion) => {
                    if (rawQuestion.answer === null || rawQuestion.answer === undefined || rawQuestion.answer === '') {
                        return;
                    }
                    details[group].push({
                        question: rawQuestion.name,
                        answer: rawQuestion.answer,
                    });
                    Object.values(rawQuestion.subQuestions).forEach((rawSubQuestion) => {
                        if (rawSubQuestion.answer === null || rawSubQuestion.answer === undefined || rawSubQuestion.answer === '') {
                            return;
                        }
                        if (rawSubQuestion.link) {
                            details[group].push({
                                question: rawSubQuestion.name,
                                answer: `<a href="${rawSubQuestion.link}">${rawSubQuestion.answer}</a>`,
                            });
                            return;
                        }
                        details[group].push({
                            question: rawSubQuestion.name,
                            answer: rawSubQuestion.answer,
                        });
                    });
                });
            });
        });

        return details;
    }

    get additionalFiles() {
        return this._additionalFiles;
    }

    get hasAssignmentWithoutEditor() {
        return !!this._assignments.find(a => !a.editor);
    }

    get hasAssignmentWithEditor() {
        return !!this._assignments.find(a => a.isInProgress);
    }

    get sampleEditFeedback() {
        return this._sampleEditStudentFeedback;
    }

    get sampleEditStudentFeedbackDeadline() {
        return this._sampleEditStudentFeedbackDeadline;
    }

    get canProvideSampleEditFeedback() {
        return moment().isBefore(this.sampleEditStudentFeedbackDeadline);
    }

    get hasBundledAssignment() {
        return this._assignments.findIndex(assignment => assignment.isBundle) !== -1;
    }

    get isDelivered() {
        return this._status === 'delivered';
    }

    get links() {
        return this._links;
    }

    get linkedOrder() {
        return this._linkedOrder;
    }
}

export default ProofreadingOrder;
